import { useAsyncStore } from '@/store/asyncStore';
import { ContentType, type CaptureEdgeKind } from '@vivport/backend/types';
import type { ObservationGroupInput } from '@/api/content-api';
import { useContentCreate } from './useContentQueries';

interface CreatePhotoOptions {
  /** The NeoChat this capture was submitted from; omitted for the manual door. */
  originProvenanceContentId?: number;
  linkToContentId?: number | null;
  observationGroup?: ObservationGroupInput | null;
}

interface UsePhotoCreateResult {
  createPhotos: (
    files: File[],
    options?: CreatePhotoOptions
  ) => Promise<{
    items: { id: number; code: string; edgeKind: CaptureEdgeKind | null }[];
    observationGroupId: number | null;
    error: unknown | null;
  }>;
}

export function usePhotoCreate(): UsePhotoCreateResult {
  const createContentMutation = useContentCreate();
  const addTask = useAsyncStore((state) => state.addTask);

  const createPhotos = async (files: File[], options: CreatePhotoOptions = {}) => {
    const { linkToContentId, originProvenanceContentId } = options;
    const created: { id: number; code: string; edgeKind: CaptureEdgeKind | null }[] = [];
    // A multi-photo submit is one observation round: the first create carries the
    // caller's observationGroup and every later create joins the group it returned.
    let observationGroupId: number | null = null;
    let observationGroup = options.observationGroup ?? null;
    // A failure part-way through must not throw away the photos already on the server:
    // the caller would report them unsent, the member would retry, and the evidence
    // would be duplicated. Stop at the first failure and hand back what was created,
    // in input order, so the caller can tell created from unsent by position.
    let error: unknown | null = null;
    for (const file of files) {
      try {
        const content = await createContentMutation.mutateAsync({
          contentType: ContentType.PHOTO,
          file,
          title: file.name,
          ...(originProvenanceContentId !== undefined ? { originProvenanceContentId } : {}),
          ...(linkToContentId ? { linkToContentId } : {}),
          ...(observationGroup ? { observationGroup } : {}),
        });
        addTask({
          title: `Processing ${file.name}`,
          description: 'Photo processing started.',
          status: 'processing',
          contentType: 'photo',
          contentId: content.id,
          payload: { photoId: content.id },
        });
        created.push({ id: content.id, code: content.code, edgeKind: content.edgeKind ?? null });
        if (content.observationGroupId != null) {
          observationGroupId = content.observationGroupId;
          observationGroup = { groupId: content.observationGroupId };
        }
      } catch (err) {
        error = err;
        break;
      }
    }
    return { items: created, observationGroupId, error };
  };

  return { createPhotos };
}
